import { Identifier, ReferenceManyField, useListContext } from "react-admin";
import { TheoryExamSimulationResult } from "../../providers/theoryExamSimulationResultsProvider";
import { LoadingIndicator } from "../../misc/LoadingIndicator";
import { Row } from "../../misc/Row";
import { CheckedIcon } from "../../icons/CheckedIcon";
import { autovioColors } from "../../misc/backofficeTheme";
import { CircleFalseIcon } from "../../icons/CircleFalseIcon";
import { Column } from "../../misc/Column";
import { Divider, Chip, Typography } from "@mui/material";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { formatDateTime } from "../../utils/calendar";

interface TheoryExamSimulationResultsViewProps {
  onClick: (simulationId: Identifier) => void;
}

export function TheoryExamSimulationResultsView(props: TheoryExamSimulationResultsViewProps) {
  const { onClick } = props;
  return (
    <ReferenceManyField
      reference="theoryExamSimulationResults"
      target="studentId"
      sort={{ field: "timestamp", order: "DESC" }}
      page={1}
      perPage={9999}
    >
      <_ResultsList onClick={onClick} />
    </ReferenceManyField>
  );
}

function _ResultsList(props: { onClick: (simulationId: Identifier) => void }) {
  const { onClick } = props;
  const { data, isLoading } = useListContext<TheoryExamSimulationResult>();
  if (isLoading) {
    return <LoadingIndicator />;
  }
  const groupedResults = new Map<string, TheoryExamSimulationResult[]>();
  data?.forEach((result) => {
    const existing = groupedResults.get(result.id);
    if (existing) {
      existing.push(result);
    } else {
      groupedResults.set(result.id, [result]);
    }
  });
  return (
    <>
      {Array.from(groupedResults.entries()).map(([id, results]) => (
        <_ResultsListItem key={id} results={results} onClick={onClick} />
      ))}
    </>
  );
}

function _ResultsListItem(props: {
  results: TheoryExamSimulationResult[];
  onClick: (simulationId: Identifier) => void;
}) {
  const { results, onClick } = props;
  if (results.length === 0) {
    return null;
  }
  return (
    <>
      <Row
        spacing={1}
        alignItems="center"
        className="_ResultsListItem"
        justifyContent="space-between"
        onClick={() => onClick(results[0].id)}
        sx={{ cursor: "pointer" }}
        py={1}
      >
        <Row spacing={1} alignItems="center">
          {results.map((result) => result.passed).every((passed) => passed) ? (
            <CheckedIcon sx={{ color: autovioColors.green, fontSize: 20 }} />
          ) : (
            <CircleFalseIcon sx={{ color: autovioColors.red, fontSize: 20 }} />
          )}
          <Column>
            <Row spacing={1} alignItems="center">
              <Typography sx={{ fontSize: 16 }}>
                {results.map((result) => (result.passed ? "Bestanden" : "Nicht bestanden")).join(" / ")}
              </Typography>
              {results.some((result) => result.cancelled) && (
                <Chip
                  label="Abgebrochen"
                  size="small"
                  sx={{ backgroundColor: "action.disabledBackground", height: "20px" }}
                />
              )}
            </Row>
            <Typography
              sx={{
                fontSize: 14,
                color: "text.secondary",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {`${results.map((result) => result.errorPoints).join(" / ")} Fehlerpunkte • ${formatDateTime(results[0].timestamp, true)}`}
            </Typography>
          </Column>
        </Row>
        <NavigateNextIcon sx={{ color: "text.secondary", fontSize: 20 }} />
      </Row>
      <Divider />
    </>
  );
}
